import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tab,
  Tabs,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ModerationFlagsList } from "./ModerationFlagsList";
import { ImageReviewList } from "./ImageReviewList";
import { ConfigModal } from "./ConfigModal";
import { useModerationStore } from "../../store/moderationStore";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useDialogState } from "../../hooks/useDialogState";

interface ModerationManagementProps {
  onBack?: () => void;
}

export const Management: React.FC<ModerationManagementProps> = ({ onBack }) => {
  const configDialog = useDialogState();
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const { loadFlags, loadReviewFlags } = useModerationStore();
  const [tab, setTab] = React.useState(0);

  const defaultOnBack = () => {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem("activeScreenId", "videoPlatform");
      window.location.reload();
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Refreshes whichever list is showing. The two read different endpoints and
    // a moderator pressing refresh means "this view", not "everything".
    try {
      if (tab === 0) {
        await loadReviewFlags("PENDING");
        // The store catches its own failures and resolves, so awaiting proves
        // only that it finished. Reading the error back is what stops a failed
        // read being reported as a successful refresh over a stale list.
        const { reviewError } = useModerationStore.getState();
        showSnackbar(
          reviewError ?? "Review queue refreshed",
          reviewError ? "error" : "success"
        );
      } else {
        await loadFlags();
        const { error } = useModerationStore.getState();
        showSnackbar(
          error ?? "Moderation flags refreshed successfully",
          error ? "error" : "success"
        );
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOpenConfig = () => {
    configDialog.openDialog();
  };

  const handleCloseConfig = () => {
    configDialog.close();
  };

  const handleConfigSubmit = () => {
    // The chat flags list, not whichever tab happens to be open. These are chat
    // moderation thresholds, and the review queue is unaffected by them.
    loadFlags();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Moderation
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Review flagged content and manage chat moderation configuration
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
          <IconButton
            onClick={onBack || defaultOnBack}
            color="primary"
            sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}
            title="Back to Video Platform"
          >
            <ArrowBackIcon />
          </IconButton>

          <IconButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            color="primary"
            sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}
            title="Refresh"
          >
            <RefreshIcon />
          </IconButton>

          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={handleOpenConfig}
            sx={{
              backgroundColor: "#7357FF",
              "&:hover": { backgroundColor: "#5a42cc" },
            }}
          >
            Configuration
          </Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_event, next) => setTab(next)}
            aria-label="moderation views"
          >
            {/* Review first: it is the one with work in it. The chat list is a
                record of what already happened. */}
            <Tab label="Review queue" id="moderation-tab-0" />
            <Tab label="Chat flags" id="moderation-tab-1" />
          </Tabs>
        </Box>

        <div role="tabpanel" hidden={tab !== 0} id="moderation-tabpanel-0">
          {tab === 0 && <ImageReviewList />}
        </div>

        <div role="tabpanel" hidden={tab !== 1} id="moderation-tabpanel-1">
          {tab === 1 && <ModerationFlagsList onRefresh={handleRefresh} />}
        </div>

        <ConfigModal
          open={configDialog.open}
          onClose={handleCloseConfig}
          onSubmit={handleConfigSubmit}
        />

        {SnackbarNode}
      </Box>
    </Box>
  );
};
